/**
 * FEATURE C2 · 2026-05-21 — Offline outbox (IndexedDB).
 *
 * O foreman fica em canteiro com 1G ruim. Quando a mutation falha por
 * network error, ela é enfileirada num IndexedDB store separado e o UI
 * mostra "salvo pra sincronizar quando voltar online". Quando o
 * `online` event dispara (ou o Service Worker recebe `sync` evt), nós
 * percorremos o outbox e fazemos POST de cada item.
 *
 * Tipos suportados (whitelist explícita — adicionar novo tipo exige
 * editar `flushOutboxItem` para mapear endpoint + body):
 *   - daily_log.create        — POST /v1/projects/:id/daily-logs
 *   - photo.upload            — POST sign + R2 PUT + complete (3 etapas)
 *   - fvs.checklist_item_update — PATCH /v1/projects/:id/fvs/:iid
 *
 * Não vai cobrir nesta fatia:
 *   - Edição complexa (só create + checkbox toggle)
 *   - Conflict resolution server-side (last-write-wins; documentar)
 *
 * Storage: DB separado `builder-outbox` v1, store `outbox` keyPath=id.
 * Mantemos isolado do `builder-drafts` (idb.ts) pra simplificar wipe
 * sem perder rascunhos não-sincronizáveis.
 */

import {
  api,
  ApiError,
  uploadPhotoFile,
} from '@/api/client';
import { useAuth, isSessionValid } from '@/store/auth.store';
import type {
  CreateDailyLogRequest,
  UpdateFvsInspectionRequest,
} from '@builder/shared';

const DB_NAME = 'builder-outbox';
const DB_VERSION = 1;
const STORE = 'outbox';

export type OutboxActionType =
  | 'daily_log.create'
  | 'photo.upload'
  | 'fvs.checklist_item_update';

export interface OutboxItemBase<T extends OutboxActionType, P> {
  id: string;
  type: T;
  /** ProjectId é usado pelo UI pra agrupar visualmente. */
  projectId: string;
  payload: P;
  createdAt: string;
  /**
   * Número de tentativas. Usado pra exponential backoff visual e pra
   * cap de retries (>5 → marca como "needs attention" mas mantém na
   * lista pra o foreman não perder o trabalho).
   */
  attempts: number;
  lastError?: string;
  lastAttemptAt?: string;
}

export type DailyLogOutboxItem = OutboxItemBase<
  'daily_log.create',
  { request: CreateDailyLogRequest }
>;

export interface PhotoUploadPayload {
  /** Stored as Blob inside IDB (structured-cloned). */
  fileBlob: Blob;
  fileName: string;
  fileType: string;
  /** Optional context fields. */
  dailyLogId?: string;
  caption?: string;
}
export type PhotoUploadOutboxItem = OutboxItemBase<'photo.upload', PhotoUploadPayload>;

export interface FvsChecklistItemUpdatePayload {
  inspectionId: string;
  request: UpdateFvsInspectionRequest;
}
export type FvsOutboxItem = OutboxItemBase<
  'fvs.checklist_item_update',
  FvsChecklistItemUpdatePayload
>;

export type OutboxItem = DailyLogOutboxItem | PhotoUploadOutboxItem | FvsOutboxItem;

// ───────────────────────────── IDB primitives

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt');
        store.createIndex('projectId', 'projectId');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

function tx(mode: IDBTransactionMode): Promise<IDBObjectStore | null> {
  return openDb().then((db) => {
    if (!db) return null;
    try {
      return db.transaction(STORE, mode).objectStore(STORE);
    } catch {
      return null;
    }
  });
}

function genId(): string {
  // Não usamos newId do backend (incompatível module path). 26 chars suficiente
  // pra evitar collision no escopo per-device.
  return (
    'obx_' +
    Date.now().toString(36) +
    Math.random().toString(36).slice(2, 10)
  );
}

// ───────────────────────────── Public API

/**
 * Enfileira uma ação no outbox. Retorna o item criado (com id atribuído).
 * Best-effort: se IDB não estiver disponível, devolve o item mas não
 * persiste — o caller pode logar mas não deve quebrar fluxo.
 */
export async function enqueueAction<I extends OutboxItem>(
  item: Omit<I, 'id' | 'createdAt' | 'attempts'>,
): Promise<I> {
  const full = {
    ...item,
    id: genId(),
    createdAt: new Date().toISOString(),
    attempts: 0,
  } as I;
  const store = await tx('readwrite');
  if (store) {
    await new Promise<void>((resolve) => {
      const req = store.add(full);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    });
  }
  notifyChange();
  return full;
}

/** Lista tudo, ordenado por createdAt asc. */
export async function listOutbox(): Promise<OutboxItem[]> {
  const store = await tx('readonly');
  if (!store) return [];
  return new Promise((resolve) => {
    const out: OutboxItem[] = [];
    const idx = store.index('createdAt');
    const req = idx.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) { resolve(out); return; }
      out.push(cursor.value as OutboxItem);
      cursor.continue();
    };
    req.onerror = () => resolve(out);
  });
}

export async function countOutbox(): Promise<number> {
  const store = await tx('readonly');
  if (!store) return 0;
  return new Promise((resolve) => {
    const req = store.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(0);
  });
}

export async function removeOutboxItem(id: string): Promise<void> {
  const store = await tx('readwrite');
  if (!store) return;
  await new Promise<void>((resolve) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
  notifyChange();
}

async function updateOutboxItem(item: OutboxItem): Promise<void> {
  const store = await tx('readwrite');
  if (!store) return;
  await new Promise<void>((resolve) => {
    const req = store.put(item);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
  notifyChange();
}

// ───────────────────────────── Flush logic

export interface FlushResult {
  attempted: number;
  succeeded: number;
  failed: number;
  remaining: number;
}

/**
 * Tenta fazer POST de cada item no outbox. Sucesso → remove. Falha de
 * rede → mantém + incrementa attempts. Falha 4xx semântica (ex: 400
 * bad request, 422 validation) → remove para evitar retry infinito
 * (último write era inválido; documentar para o foreman).
 *
 * Idempotência: não há gate global. O caller deve gatekeear (não
 * chamar duas vezes em paralelo). O service worker `sync` chama uma
 * vez por evento.
 */
export async function flushOutbox(): Promise<FlushResult> {
  // Se browser reporta offline, não desperdiça round-trip.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const remaining = await countOutbox();
    return { attempted: 0, succeeded: 0, failed: 0, remaining };
  }
  // P3 fix · 2026-05-22 — gate por sessão válida.
  // Sem auth (página pública, sessão expirada), os endpoints autenticados
  // do outbox respondem 401 e sujam o console nas rotas públicas
  // (`/leilao/:token`, `/compartilhado/...`, etc). O autoflush dispara no
  // mount do AppRoot, então o gate precisa estar aqui (não só no caller),
  // pra cobrir o boot e o `online` event. Itens ficam no outbox até a
  // próxima sessão válida — onSuccess do login fará o flush.
  try {
    const auth = useAuth.getState();
    if (!isSessionValid(auth)) {
      const remaining = await countOutbox();
      return { attempted: 0, succeeded: 0, failed: 0, remaining };
    }
  } catch {
    // Zustand pode não estar pronto em testes; nesse caso seguimos
    // (testes mockam o store ou stubam fetch).
  }
  const items = await listOutbox();
  let succeeded = 0;
  let failed = 0;
  for (const item of items) {
    try {
      await flushOutboxItem(item);
      await removeOutboxItem(item.id);
      succeeded += 1;
    } catch (err) {
      failed += 1;
      const isApi = err instanceof ApiError;
      const isFatal4xx =
        isApi && err.status >= 400 && err.status < 500 && err.status !== 408 && err.status !== 429;
      if (isFatal4xx) {
        // Drop irrecuperável — re-tentar uma 400 vai dar 400 pra sempre.
        // eslint-disable-next-line no-console
        console.warn('[outbox] dropping unrecoverable item', item.id, err.status, err.message);
        await removeOutboxItem(item.id);
      } else {
        await updateOutboxItem({
          ...item,
          attempts: item.attempts + 1,
          lastError: err instanceof Error ? err.message : String(err),
          lastAttemptAt: new Date().toISOString(),
        });
      }
    }
  }
  const remaining = await countOutbox();
  return { attempted: items.length, succeeded, failed, remaining };
}

async function flushOutboxItem(item: OutboxItem): Promise<void> {
  switch (item.type) {
    case 'daily_log.create': {
      await api.createDailyLog(item.projectId, item.payload.request);
      return;
    }
    case 'photo.upload': {
      const p = item.payload;
      const signed = await api.requestPhotoUpload(item.projectId, {
        filename: p.fileName,
        // Garante que cai num mime válido do enum. Quando o browser não
        // detecta (file.type='') usamos jpeg como fallback "câmera".
        mimeType: (p.fileType || 'image/jpeg') as 'image/jpeg',
        sizeBytes: p.fileBlob.size,
        dailyLogId: p.dailyLogId,
        caption: p.caption,
      });
      await uploadPhotoFile(signed, p.fileBlob);
      await api.completePhotoUpload({ photoId: signed.photoId });
      return;
    }
    case 'fvs.checklist_item_update': {
      const p = item.payload;
      await api.updateFvsInspection(item.projectId, p.inspectionId, p.request);
      return;
    }
    default: {
      // Exhaustive check — TS narrowing garante que adicionando um novo
      // type sem case aqui dá erro de compile.
      const _exhaustive: never = item;
      throw new Error(`Unknown outbox item type: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

// ───────────────────────────── Reactive change notifier

const CHANGE_EVENT = 'builder:outbox-changed';

function notifyChange(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch { /* noop */ }
}

/** Subscribe — usado pelo OfflineIndicator hook. Retorna cleanup. */
export function onOutboxChange(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = () => cb();
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

/**
 * Tenta agendar background sync via Service Worker. Em browsers sem
 * suporte (Safari iOS), cai pro listener `online` + chamada manual de
 * `flushOutbox()`. Best-effort — nunca lança.
 */
export async function requestBackgroundSync(): Promise<boolean> {
  if (typeof navigator === 'undefined') return false;
  const sw = navigator.serviceWorker;
  if (!sw) return false;
  try {
    const reg = await sw.ready;
    // The SyncManager API isn't in the standard lib.dom yet — narrow defensively.
    const syncReg = (reg as unknown as { sync?: { register: (tag: string) => Promise<void> } }).sync;
    if (!syncReg) return false;
    await syncReg.register('builder-outbox-sync');
    return true;
  } catch {
    return false;
  }
}
